import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  CurrencyDisplay,
  CustomTable,
  FormCard,
  ModalError,
  Page,
  SectionLabel,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, stringToArray, wordCapitalize } from "utils";
import {
  MOCK_DETAIL_CLAIM_LOG,
  MOCK_CLAIMED_SONGS,
  MOCK_CLAIMED_SONGS_CHILDREN,
} from "../mockData";

const DetailPotentialClaimLog = () => {
  const classes = globalStyles();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const { id } = useParams();
  const pageTitle = "Detail Potential Claim Log";

  const [loadingPage, setLoadingPage] = useState(true);
  const [pageDetail, setPageDetail] = useState(MOCK_DETAIL_CLAIM_LOG);
  const [dataTable, setDataTable] = useState(MOCK_CLAIMED_SONGS);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [loadingChildrenTable, setLoadingChildrenTable] = useState(false);
  const [queryParamsChildren, setQueryParamsChildren] = useState({
    page: 1,
    size: 10,
    song_id: 0,
  });
  const [dataTableChildren, setDataTableChildren] = useState(
    MOCK_CLAIMED_SONGS_CHILDREN
  );
  const [tablePageCountChildren, setTablePageCountChildren] = useState(1);
  const { status } = pageDetail || {};
  const errorLog = status === "error";

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeQueryParamsChildren = (value, key) => {
    setQueryParamsChildren(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDetail = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/admin/potential-claim/claim-logs/${id}`,
        { headers }
      );
      const data = res?.data?.data;
      setPageDetail(data || {});
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };

  const getDataTable = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/admin/potential-claim/claim-logs/${id}/songs`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data || {};
      const listData = Array.isArray(data) ? data : data?.data || [];
      setDataTable(listData);
      const pageCount = getTotalPage(
        meta?.total || listData.length,
        queryParams?.size
      );
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
      setDataTable([]);
      setTableTotalPage(1);
    }
  };

  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [];
    if (isFirstLoad.current) promises.push(getDetail());
    promises.push(getDataTable());
    try {
      await Promise.all(promises);
    } catch (error) {
      // Fallbacks used in individual catches
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  const getDataChildrenTable = async () => {
    const { page, size } = queryParamsChildren;
    setLoadingChildrenTable(true);
    try {
      const res = await axios.get(
        `${hardBaseUrl}/admin/potential-claim/claim-logs/${id}/songs/${queryParamsChildren?.song_id}`,
        {
          headers,
          params: { page, size },
        }
      );
      const { data, meta } = res?.data || {};
      const listData = Array.isArray(data) ? data : data?.data || [];
      setDataTableChildren(listData);
      const pageCount = getTotalPage(
        meta?.total || listData.length,
        size
      );
      setTablePageCountChildren(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
      setDataTableChildren([]);
      setTablePageCountChildren(1);
    } finally {
      setLoadingChildrenTable(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [queryParams]);

  useEffect(() => {
    if (queryParamsChildren?.song_id) {
      getDataChildrenTable();
    }
  }, [queryParamsChildren]);

  return (
    <Page className={classes.root} title={pageTitle}>
      <Container maxWidth={false}>
        <HeaderTitle
          title={pageTitle}
          breadcrumbData={breadcrumbData({ pageTitle })}
          backButton
        />
        <Divider className={classes.divider} />
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <SectionLabel
              title="Detail of potential claim log"
              subTitle="View the detail data for potential claim log"
            />
          </Grid>
          {status && (
            <Grid item>
              <StatusChip
                label={wordCapitalize(status) || "-"}
                type={
                  status === "finished" || status === "finish"
                    ? "success"
                    : status === "time-out" || status === "error"
                    ? "danger"
                    : status === "started"
                    ? "warning"
                    : ""
                }
              />
            </Grid>
          )}
        </Grid>
        {loadingPage ? (
          <Box my={2}>
            <Skeleton variant="rounded" height="170px" animation="wave" />
          </Box>
        ) : (
          <FormCard my={2}>
            <Grid container rowGap={1.5}>
              {mockfieldForm({ errorLog }).map((field, index) => (
                <Fragment key={index}>
                  <Grid item xs={12} sm={4} md={3} lg={2}>
                    <Typography
                      className={classes?.fieldTitle}
                      fontWeight={500}
                      color="#4B5563"
                    >
                      {field?.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={9} lg={10}>
                    <Typography
                      className={classes?.fieldValue}
                      fontWeight={600}
                      color="#111827"
                    >
                      {field?.type === "number" ? (
                        <CurrencyDisplay
                          value={
                            pageDetail?.[field?.value] ??
                            pageDetail?.[field?.fallbackValue]
                          }
                          prefix={field?.prefix}
                          decimalScale={field?.prefix === "Rp" ? 2 : undefined}
                          textAlign="left"
                        />
                      ) : (
                        pageDetail?.[field?.value] ||
                        pageDetail?.[field?.fallbackValue] ||
                        "-"
                      )}
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </FormCard>
        )}
        {!errorLog && (
          <CustomTable
            isLoading={loadingPage}
            columnTable={columnTable}
            data={dataTable || []}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isExpandable
            expandedRowKey="id"
            handleExpandableRow={item =>
              handleChangeQueryParamsChildren(
                item?.song_id || item?.id,
                "song_id"
              )
            }
            renderExpandedRow={() => (
              <Box py={1} px={1} bgcolor="#FFFFFF">
                <CustomTable
                  isLoading={loadingChildrenTable}
                  columnTable={columnTableChildren}
                  data={dataTableChildren || []}
                  page={queryParamsChildren?.page}
                  pageSize={queryParamsChildren?.size}
                  pageCount={tablePageCountChildren}
                  handleChangePage={(_, page) =>
                    handleChangeQueryParamsChildren(page, "page")
                  }
                  handleChangePageSize={e =>
                    handleChangeQueryParamsChildren(e?.target?.value, "size")
                  }
                  border="none"
                  borderRadius={0}
                  maxHeight={300}
                  tableSize="small"
                />
              </Box>
            )}
            my={2}
          />
        )}
      </Container>
    </Page>
  );
};

const mockfieldForm = ({ errorLog }) =>
  errorLog
    ? [{ label: "Error Message", value: "error_details" }]
    : [
        {
          label: "Document Number",
          value: "document_number",
          fallbackValue: "doc_no",
        },
        {
          label: "Publisher",
          value: "publisher_name",
          fallbackValue: "publisher",
        },
        { label: "DSP", value: "dsp_name", fallbackValue: "dsp" },
        {
          label: "Total Traffic",
          value: "total_streams_claimed",
          fallbackValue: "total_traffic",
          type: "number",
        },
        {
          label: "Total Revenue",
          value: "revenue_claimed",
          fallbackValue: "total_revenue",
          type: "number",
          prefix: "Rp",
        },
      ];

export const breadcrumbData = ({ pageTitle }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Song Claim Monitoring",
    link: "/admin/potential-claim",
  },
  {
    label: pageTitle,
    active: true,
  },
];

const columnTable = [
  {
    name: "song_title",
    title: "Song title",
    renderText: item => <Typography fontWeight={700}>{item || "-"}</Typography>,
  },
  {
    name: "isrc",
    title: "ISRC Code",
    renderText: (item, row) => {
      const isrcVal = item || row?.isrc_code || "-";
      return isrcVal.includes("|") ? (
        <ArrayChip list={stringToArray(isrcVal, "|")} />
      ) : (
        isrcVal
      );
    },
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    renderText: (item, row) => (
      <CurrencyDisplay value={item ?? row?.total_traffic ?? 0} />
    ),
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: (item, row) => (
      <CurrencyDisplay
        value={item ?? row?.total_revenue ?? 0}
        prefix="Rp"
        decimalScale={2}
      />
    ),
  },
  {
    name: "period_date",
    title: "Period date",
    renderText: (item, row) => item || row?.period || "-",
  },
];

const columnTableChildren = [
  {
    name: "song_title",
    title: "Song title",
    renderText: item => <Typography fontWeight={700}>{item || "-"}</Typography>,
  },
  {
    name: "isrc",
    title: "ISRC Code",
    renderText: (item, row) => {
      const isrcVal = item || row?.isrc_code || "-";
      return isrcVal.includes("|") ? (
        <ArrayChip list={stringToArray(isrcVal, "|")} />
      ) : (
        isrcVal
      );
    },
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    renderText: (item, row) => (
      <CurrencyDisplay value={item ?? row?.total_traffic ?? 0} />
    ),
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: (item, row) => (
      <CurrencyDisplay
        value={item ?? row?.total_revenue ?? 0}
        prefix="Rp"
        decimalScale={2}
      />
    ),
  },
  {
    name: "period_date",
    title: "Period date",
    renderText: (item, row) => item || row?.period || "-",
  },
];

export default DetailPotentialClaimLog;
